import React, { useRef, useEffect, useState, useCallback } from "react";
import * as Tooltip from "@radix-ui/react-tooltip";
import { ChevronDown, ChevronRight } from "lucide-react";
import ActivitySequenceRow from "./ActivitySequenceRow";
import LoopSegmentRow from "./LoopSegmentRow";
import RepetitionMetricsCell from "./RepetitionMetricsCell";
import { useLegendColumn, LegendColumnHeader, LegendColumnCell } from "./LegendColumn";
import "./VariantTableExtended.css";

const formatLeadTime = (hours) => {
  if (typeof hours !== "number" || isNaN(hours) || hours < 0) return "–";

  const days = hours / 24;

  if (hours < 1) {
    return `${hours.toFixed(1)} h`;
  } else if (days < 1) {
    return `${hours.toFixed(1)} h`;
  } else if (days < 30) {
    return `${days.toFixed(2)} d`;
  } else {
    const months = days / 30;
    return `${months.toFixed(2)} mo`;
  }
};

const formatCount = (num) => {
  if (typeof num !== "number" || isNaN(num)) return "–";
  if (num < 1000) return num.toString();
  return (num / 1000).toFixed(2) + "K";
};

const getMedian = (values) => {
  if (!Array.isArray(values) || values.length === 0) return "–";
  const sorted = values.slice().sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
};

const getLoopRepetitionRange = (loopSegments) => {
  const loopSegmentsOnly = (loopSegments || []).filter(
    (s) => s.is_loop && typeof s.count_min === "number" && typeof s.count_max === "number"
  );
  if (loopSegmentsOnly.length === 0) return null;
  return {
    min: Math.min(...loopSegmentsOnly.map((s) => s.count_min)),
    max: Math.max(...loopSegmentsOnly.map((s) => s.count_max)),
  };
};

const renderAbcXyzBadge = (abcXyzClass) => {
  if (!abcXyzClass) {
    return <span className="text-gray-400">—</span>;
  }
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
      {abcXyzClass}
    </span>
  );
};

// Tooltip component for ABC/XYZ header info
const AbcXyzHeaderInfo = () => (
  <Tooltip.Provider delayDuration={150}>
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <button
          type="button"
          className="inline-flex text-gray-400 hover:text-gray-600 transition-colors align-middle"
          aria-label="About the ABC/XYZ classification"
          onClick={(e) => e.stopPropagation()}
        >
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          side="top"
          align="start"
          sideOffset={6}
          className="max-w-[300px] bg-white border border-gray-200 rounded-md shadow-lg z-50"
        >
          <div className="px-2.5 py-1.5 border-b border-gray-100 bg-primary-50 rounded-t-md">
            <span className="text-xs font-medium text-primary-700">
              ABC/XYZ Classification
            </span>
          </div>
          <div className="px-2.5 py-1.5 text-xs leading-normal text-gray-500 space-y-1">
            <p>
              <span className="font-medium text-gray-700">ABC</span> ranks variants by
              case coverage: A = first 80%, B = next 15%, C = remaining 5%.
            </p>
            <p>
              <span className="font-medium text-gray-700">XYZ</span> rates temporal
              stability using variation coefficient: X &lt; 25%, Y = 25–50%, Z &gt; 50%.
            </p>
            <p>
              <span className="font-medium text-gray-700">Example:</span> AX =
              high-volume and stable; AZ = high-volume but unstable.
            </p>
          </div>
          <Tooltip.Arrow className="fill-white" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  </Tooltip.Provider>
);

/**
 * VariantTableExtended - Extended version of VariantTable with activity sequence column.
 *
 * @param {Object} props
 * @param {Array} props.variants - All variants
 * @param {Array} props.currentVariants - Current page variants
 * @param {Array} props.selectedVariants - Selected variant IDs
 * @param {Function} props.handleToggleSelect - Toggle selection callback
 * @param {Function} props.toggleSelectAll - Toggle all selection callback
 * @param {string} props.sortKey - Current sort key
 * @param {string} props.sortOrder - Current sort order
 * @param {Function} props.handleColumnSort - Column sort callback
 * @param {boolean} props.loading - Loading state
 * @param {Object} props.others - "Others" row data
 * @param {Object} props.colorPalette - Map of activity_code to color
 * @param {Object} props.activityCatalog - Map of activity_code to { code, name }
 */
const VariantTableExtended = ({
  variants,
  currentVariants,
  selectedVariants,
  handleToggleSelect,
  toggleSelectAll,
  sortKey,
  sortOrder,
  handleColumnSort,
  loading,
  others,
  colorPalette,
  activityCatalog,
}) => {
  // Legend column state
  const { isExpanded, toggleExpanded, sortedActivities } = useLegendColumn(activityCatalog, colorPalette);

  // Per-row expand state for the Activity Sequence column, keyed by variant_id
  // so it survives re-sorts and only toggles when the user clicks the caret.
  const [expandedRows, setExpandedRows] = useState(() => new Set());

  const toggleRowExpanded = useCallback((variantId) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(variantId)) {
        next.delete(variantId);
      } else {
        next.add(variantId);
      }
      return next;
    });
  }, []);

  // Refs for synchronized scrolling
  const columnScrollRef = useRef(null);
  const contentRefsRef = useRef([]);
  const [scrollLeft, setScrollLeft] = React.useState(0);
  const [maxScrollWidth, setMaxScrollWidth] = React.useState(0);

  // Handle scroll on the column container
  const handleColumnScroll = (e) => {
    const newScrollLeft = e.target.scrollLeft;
    setScrollLeft(newScrollLeft);
  };

  // Calculate max content width from all rows
  useEffect(() => {
    if (contentRefsRef.current.length > 0) {
      const maxWidth = Math.max(
        ...contentRefsRef.current.map((ref) => ref?.scrollWidth || 0)
      );
      if (maxWidth > 0) {
        setMaxScrollWidth(maxWidth);
      }
    }
  }, [currentVariants]);

  // Clear refs when variants change
  useEffect(() => {
    contentRefsRef.current = [];
  }, [currentVariants]);

  const renderSortArrow = (key) =>
    sortKey === key ? (sortOrder === "asc" ? "↑" : "↓") : "";

  const othersStepsArray = variants
    .filter((v) => !selectedVariants.includes(v.variant_id))
    .map((v) => v.num_steps);
  const othersMedianSteps = getMedian(othersStepsArray);

  return (
    <div className="overflow-auto h-full flex flex-col">
      <div className="flex-1 overflow-auto">
      <table className="w-full text-sm border-collapse" style={{ tableLayout: 'fixed' }}>
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr className="border-b border-gray-200">
            <th className="px-4 py-3 text-left" style={{ width: '60px' }}>
              <input
                type="checkbox"
                checked={selectedVariants.length === variants.length}
                onChange={toggleSelectAll}
                disabled={loading}
                className="cursor-pointer"
              />
            </th>
            <th
              onClick={() => handleColumnSort("variant_id")}
              className="px-4 py-3 text-left font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
              style={{ width: '120px' }}
            >
              Variant {renderSortArrow("variant_id")}
            </th>
            <th
              onClick={() => handleColumnSort("trace_count")}
              className="px-4 py-3 text-right font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
              style={{ width: '100px' }}
            >
              Count {renderSortArrow("trace_count")}
            </th>
            <th
              onClick={() => handleColumnSort("percentage")}
              className="px-4 py-3 text-left font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
              style={{ width: '200px' }}
            >
              Cases Covered {renderSortArrow("percentage")}
            </th>
            <th
              onClick={() => handleColumnSort("abc_xyz_class")}
              className="px-4 py-3 text-left font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
              style={{ width: '110px' }}
            >
              <span className="inline-flex items-center gap-1">
                ABC/XYZ {renderSortArrow("abc_xyz_class")}
                <AbcXyzHeaderInfo />
              </span>
            </th>
            <th
              onClick={() => handleColumnSort("num_steps")}
              className="px-4 py-3 text-right font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
              style={{ width: '100px' }}
            >
              Steps {renderSortArrow("num_steps")}
            </th>
            <th
              onClick={() => handleColumnSort("median_lead_time")}
              className="px-4 py-3 text-right font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
              style={{ width: '150px' }}
            >
              Median Lead Time {renderSortArrow("median_lead_time")}
            </th>
            <th className="text-left font-semibold text-gray-700" style={{ width: '700px' }}>
              <div className="px-4 py-3" title="Expand a row to see its full activity sequence and repetition metrics.">
                Activity Sequence
              </div>
            </th>
            <LegendColumnHeader
              isExpanded={isExpanded}
              onToggle={toggleExpanded}
              activityCount={sortedActivities.length}
            />
          </tr>
        </thead>
        <tbody>
          {currentVariants.map((v, index) => {
            const isRowExpanded = expandedRows.has(v.variant_id);
            const hasLoopSegments =
              Array.isArray(v.loop_segments) && v.loop_segments.length > 0;
            return (
              <tr
                key={v.variant_id}
                className="border-b border-gray-100 hover:bg-gray-50 transition-colors [&>td]:align-top"
              >
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    disabled={loading}
                    checked={selectedVariants.includes(v.variant_id)}
                    onChange={() => handleToggleSelect(v.variant_id)}
                    className="cursor-pointer"
                  />
                </td>
                <td className="px-4 py-3 text-gray-900">{v.variant_id}</td>
                <td className="px-4 py-3 text-right text-gray-900 font-medium">
                  {v.trace_count}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="flex-grow bg-gray-200 h-2.5 rounded-full overflow-hidden min-w-[80px] max-w-[120px]">
                      <div
                        className="bg-blue-600 h-full rounded-full transition-all"
                        style={{ width: `${v.percentage}%` }}
                      />
                    </div>
                    <span className="text-gray-700 font-medium min-w-[45px] text-right">
                      {v.percentage}%
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {renderAbcXyzBadge(v.abc_xyz_class)}
                </td>
                <td className="px-4 py-3 text-right text-gray-900">
                  {v.num_steps}
                </td>
                <td className="px-4 py-3 text-right text-gray-900">
                  {formatLeadTime(v.lead_time_stats?.median)}
                </td>
                <td className="px-0 py-0" style={{ width: '700px' }}>
                  <div className="px-4 py-3">
                    <div className="flex items-start gap-2">
                      <button
                        type="button"
                        onClick={() => toggleRowExpanded(v.variant_id)}
                        aria-expanded={isRowExpanded}
                        aria-label={isRowExpanded ? "Collapse variant details" : "Expand variant details"}
                        className="flex-shrink-0 mt-1 text-gray-500 hover:text-primary-700 transition-colors"
                        title={isRowExpanded ? "Hide full sequence and metrics" : "Show full sequence and metrics"}
                      >
                        {isRowExpanded ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </button>

                      <div className="flex-1 min-w-0">
                        {isRowExpanded ? (
                          hasLoopSegments ? (
                            <LoopSegmentRow
                              loopSegments={v.loop_segments}
                              colorPalette={colorPalette}
                              activityCatalog={activityCatalog}
                              wrap
                            />
                          ) : (
                            <ActivitySequenceRow
                              activitySequence={v.activity_sequence || []}
                              colorPalette={colorPalette}
                              activityCatalog={activityCatalog}
                              wrap
                            />
                          )
                        ) : (
                          <div className="overflow-hidden" style={{ position: 'relative' }}>
                            <div
                              ref={(el) => {
                                if (el && !contentRefsRef.current.includes(el)) {
                                  contentRefsRef.current.push(el);
                                }
                              }}
                              style={{
                                transform: `translateX(-${scrollLeft}px)`,
                                transition: 'transform 0s',
                                willChange: 'transform',
                              }}
                            >
                              {hasLoopSegments ? (
                                <LoopSegmentRow
                                  loopSegments={v.loop_segments}
                                  colorPalette={colorPalette}
                                  activityCatalog={activityCatalog}
                                />
                              ) : (
                                <ActivitySequenceRow
                                  activitySequence={v.activity_sequence || []}
                                  colorPalette={colorPalette}
                                  activityCatalog={activityCatalog}
                                />
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {isRowExpanded && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <RepetitionMetricsCell
                          isLoopCompressed={hasLoopSegments}
                          uniqueActivities={v.unique_activities}
                          repetitionCount={v.repetition_count}
                          repeatedActivities={v.repeated_activities}
                          loopsCount={
                            hasLoopSegments
                              ? v.loop_segments.filter((s) => s.is_loop).length
                              : null
                          }
                          repetitionsRange={
                            hasLoopSegments
                              ? getLoopRepetitionRange(v.loop_segments)
                              : null
                          }
                          medianLoopRepeats={
                            hasLoopSegments
                              ? v.loop_segments
                                  // Numeric only: in abstract mode count_display
                                  // is the placeholder "N".
                                  .filter(
                                    (s) =>
                                      s.is_loop &&
                                      typeof s.count_display === "number"
                                  )
                                  .map((s) => s.count_display)
                                  .join(", ") || null
                              : null
                          }
                        />
                      </div>
                    )}
                  </div>
                </td>
                <LegendColumnCell
                  isExpanded={isExpanded}
                  sortedActivities={sortedActivities}
                  isFirstRow={index === 0}
                  rowSpan={currentVariants.length + 1}
                />
              </tr>
            );
          })}

          {/* Scrollbar Row - seamlessly integrated into Activity Sequence column */}
          {currentVariants.length > 0 && maxScrollWidth > 0 && (
            <tr className="bg-white border-b border-gray-100">
              <td colSpan="7"></td>
              <td style={{ width: '700px', padding: 0 }}>
                <div className="px-4" style={{ paddingTop: '2px', paddingBottom: '2px' }}>
                  <div
                    ref={columnScrollRef}
                    onScroll={handleColumnScroll}
                    className="overflow-x-auto overflow-y-hidden unified-activity-scrollbar"
                    style={{
                      height: '12px',
                      scrollbarWidth: 'thin',
                      scrollbarColor: '#cbd5e1 #f1f5f9',
                      backgroundColor: 'transparent',
                    }}
                  >
                    {/* Phantom content to create scrollbar with correct width */}
                    <div style={{ width: `${maxScrollWidth}px`, height: '1px' }} />
                  </div>
                </div>
              </td>
            </tr>
          )}

          {/* Others Row */}
          <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
            <td className="px-4 py-3"></td>
            <td className="px-4 py-3 text-gray-900">
              <strong>Others</strong>
            </td>
            <td className="px-4 py-3 text-right text-gray-700">
              {formatCount(others.object_count)}
            </td>
            <td className="px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="flex-grow bg-gray-200 h-2.5 rounded-full overflow-hidden min-w-[80px] max-w-[120px]">
                  <div
                    className="bg-blue-600 h-full rounded-full transition-all"
                    style={{ width: `${Math.max(others.percentage, 0.5)}%` }}
                    title={`Exact: ${others.percentage.toFixed(2)}%`}
                  />
                </div>
                <span className="text-gray-700 font-medium min-w-[45px] text-right">
                  {others.percentage === 0
                    ? "0%"
                    : others.percentage < 1
                    ? "<1%"
                    : `${others.percentage.toFixed(2)}%`}
                </span>
              </div>
            </td>
            <td className="px-4 py-3 text-gray-400">—</td>
            <td className="px-4 py-3 text-right text-gray-700">
              {formatCount(othersMedianSteps)}
            </td>
            <td className="px-4 py-3 text-right text-gray-700">
              {formatLeadTime(others.median_lead_time)}
            </td>
            <td className="px-4 py-3 text-gray-500 italic text-xs" style={{ width: '700px' }}>
              <div>Unselected variants</div>
            </td>
            <td className="px-2 py-3 bg-gray-50 border-b border-gray-200"></td>
          </tr>
        </tbody>
      </table>
      </div>
    </div>
  );
};

export default VariantTableExtended;
